'use client';

/**
 * @fileoverview Ember NOW marker for the Discovery v2 single-day timeline
 * (WEB-671 / DPL-20).
 *
 * Sits between the In-progress and Still-ahead groups. The clock is the only
 * thing that ticks — group membership lives on the list and must not re-render
 * on this interval. Cadence matches {@link DiscoverCityClock}: once per minute,
 * aligned to the wall-clock minute boundary.
 *
 * Injectable `now` freezes the clock for RTL / Storybook. Production forwards
 * the shared Discover clock (`Date` or pending `null`).
 *
 * Reduced motion (O-5): the pulse settles to a static lit ember. Do not
 * introduce `--t-*` tokens or a custom cubic-bezier here.
 */

import * as React from 'react';
import { useEffect, useState } from 'react';

import { formatSpineNowLabel } from '@/lib/discover/timelineClock';

const TICK_INTERVAL_MS = 60_000;

/** Width-stable placeholder so the live clock never shifts the rule. */
export const DISCOVER_NOW_CLOCK_PLACEHOLDER = '--:-- PM';

export interface DiscoverNowMarkerProps {
  /**
   * IANA zone the clock renders in — the city's zone, not the browser's.
   * Matches the list's `cityTimezone` so the marker and the group copy agree.
   */
  readonly timeZone: string;
  /**
   * Shared timeline instant (WEB-779). `Date` freezes the clock. `null` is
   * provider-pending — stay on the hydration-safe placeholder and do not
   * start a local timer. Omitted (`undefined`) is uncontrolled (standalone
   * stories): tick after mount.
   */
  readonly now?: Date | null;
  readonly className?: string;
}

/** Formats `3:00 PM` in `timeZone` — same helper as the row start face. */
export function formatNowMarkerClock(date: Date, timeZone: string): string {
  return formatSpineNowLabel(date, timeZone);
}

export function DiscoverNowMarker({
  timeZone,
  now,
  className,
}: DiscoverNowMarkerProps): React.ReactElement {
  const [value, setValue] = useState<string>(() =>
    now instanceof Date ? formatNowMarkerClock(now, timeZone) : DISCOVER_NOW_CLOCK_PLACEHOLDER,
  );

  useEffect(() => {
    if (now instanceof Date) {
      setValue(formatNowMarkerClock(now, timeZone));
      return;
    }
    if (now === null) {
      setValue(DISCOVER_NOW_CLOCK_PLACEHOLDER);
      return;
    }
    const tick = (): void => setValue(formatNowMarkerClock(new Date(), timeZone));
    tick();
    let intervalId: number | undefined;
    const msToNextMinute = TICK_INTERVAL_MS - (Date.now() % TICK_INTERVAL_MS);
    const timeoutId = window.setTimeout(() => {
      tick();
      intervalId = window.setInterval(tick, TICK_INTERVAL_MS);
    }, msToNextMinute);
    return () => {
      window.clearTimeout(timeoutId);
      if (intervalId !== undefined) window.clearInterval(intervalId);
    };
  }, [timeZone, now]);

  return (
    <div
      className={className ? `dv-now ${className}` : 'dv-now'}
      data-testid="discover-now-marker"
      role="separator"
      aria-label={`Now, ${value}`}
    >
      <span className="dv-now__tag">NOW</span>
      <time
        className="dv-now__clock"
        data-testid="discover-now-marker-clock"
        dateTime={now instanceof Date ? now.toISOString() : undefined}
      >
        {value}
      </time>
      <span className="dv-now__rule" aria-hidden="true">
        <span className="dv-now__pulse" data-testid="discover-now-marker-pulse" />
      </span>
    </div>
  );
}
